import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";

const ROOT_ATTR = "data-cascade-dropdown-root";

/**
 * @typedef {Object} CascadeDropDownItem
 * @property {string} id
 * @property {string} label
 * @property {boolean} [disabled]
 * @property {() => void} [onSelect] Called for leaf items when chosen (then menu closes).
 * @property {CascadeDropDownItem[]} [children] Nested items shown in a flyout to the right.
 */

/**
 * Portaled cascading menu: primary column plus optional nested columns on hover.
 * Intended for context menus or compact action pickers. Closes on outside pointer-down
 * (capture), Escape, or when `open` becomes false.
 *
 * @param {Object} props
 * @param {boolean} props.open
 * @param {() => void} props.onClose
 * @param {DOMRect|null} props.anchorRect Viewport rect used to position the menu (e.g. `el.getBoundingClientRect()`).
 * @param {CascadeDropDownItem[]} props.items
 * @param {string} [props.className] Extra classes on the root panel.
 * @param {number} [props.offsetY=4] Gap below anchor.
 */
export default function CascadeDropDown({
  open,
  onClose,
  anchorRect,
  items = [],
  className = "",
  offsetY = 4,
}) {
  const rootRef = useRef(null);
  const [hoverParentId, setHoverParentId] = useState(null);
  const leaveTimerRef = useRef(null);

  const clearLeaveTimer = useCallback(() => {
    if (leaveTimerRef.current != null) {
      window.clearTimeout(leaveTimerRef.current);
      leaveTimerRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (!open) {
      setHoverParentId(null);
      clearLeaveTimer();
    }
  }, [open, clearLeaveTimer]);

  useEffect(() => () => clearLeaveTimer(), [clearLeaveTimer]);

  const position = useMemo(() => {
    if (!open || !anchorRect) {
      return null;
    }
    const margin = 8;
    const top = anchorRect.bottom + offsetY;
    const left = Math.max(
      margin,
      Math.min(anchorRect.left, window.innerWidth - margin - 200)
    );
    return { top, left };
  }, [anchorRect, open, offsetY]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }
    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
      }
    };
    const onPointerDown = (e) => {
      const root = rootRef.current;
      if (root && root.contains(e.target)) {
        return;
      }
      onClose();
    };
    window.addEventListener("keydown", onKeyDown, true);
    window.addEventListener("pointerdown", onPointerDown, true);
    return () => {
      window.removeEventListener("keydown", onKeyDown, true);
      window.removeEventListener("pointerdown", onPointerDown, true);
    };
  }, [open, onClose]);

  useLayoutEffect(() => {
    if (!open || !position || !rootRef.current) {
      return;
    }
    const el = rootRef.current;
    const rect = el.getBoundingClientRect();
    let { top, left } = position;
    if (rect.right > window.innerWidth - 8) {
      left = Math.max(8, window.innerWidth - rect.width - 8);
    }
    if (rect.bottom > window.innerHeight- 8) {
      top = Math.max(8, window.innerHeight - rect.height - 8);
    }
    el.style.top = `${top - 2}px`;
    el.style.left = `${left}px`;
  }, [open, position]);

  const handleLeafSelect = useCallback(
    (item) => {
      if (item.disabled) {
        return;
      }
      item.onSelect?.();
      onClose();
    },
    [onClose]
  );

  const scheduleParentLeave = useCallback(() => {
    clearLeaveTimer();
    leaveTimerRef.current = window.setTimeout(() => {
      setHoverParentId(null);
    }, 160);
  }, [clearLeaveTimer]);

  const renderLeafRow = (item, depth) => {
    const pad = depth > 0 ? "pl-3 pr-2" : "px-3";
    return (
      <button
        key={item.id}
        type="button"
        disabled={Boolean(item.disabled)}
        className={`${pad} flex w-full items-center justify-between py-0 text-left text-sm text-gray-800 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-40`}
        onClick={() => handleLeafSelect(item)}
      >
        <span className="truncate">{item.label}</span>
      </button>
    );
  };

  const renderBranchRow = (item, depth) => {
    const isOpen = hoverParentId === item.id;
    const pad = depth > 0 ? "pl-3 pr-2" : "px-3";
    return (
      <div
        key={item.id}
        className="relative"
        onMouseEnter={() => {
          clearLeaveTimer();
          setHoverParentId(item.id);
        }}
        onMouseLeave={scheduleParentLeave}
      >
        <div
          className={`${pad} flex cursor-default items-center justify-between py-0 text-sm text-gray-800 hover:bg-gray-100`}
        >
          <span className="truncate">{item.label}</span>
          <span className="pl-2 text-xs text-gray-400" aria-hidden>
            ›
          </span>
        </div>
        {isOpen && item.children?.length ? (
          <div
            className="absolute left-full top-0 z-10 ml-1 min-w-[10rem] rounded-md border border-gray-200 bg-white py-0 shadow-lg"
            onMouseEnter={() => {
              clearLeaveTimer();
              setHoverParentId(item.id);
            }}
            onMouseLeave={scheduleParentLeave}
          >
            {item.children.map((child) =>
              child.children?.length
                ? renderBranchRow(child, depth + 1)
                : renderLeafRow(child, depth + 1)
            )}
          </div>
        ) : null}
      </div>
    );
  };

  if (!open || !position) {
    return null;
  }

  return createPortal(
    <div
      ref={rootRef}
      {...{ [ROOT_ATTR]: "" }}
      className={`fixed z-[200] min-w-[10rem] rounded-md border border-gray-200 bg-white py-0 shadow-lg font-inter ${className}`}
      style={{ top: position.top, left: position.left }}
      role="menu"
    >
      {items.map((item) =>
        item.children?.length
          ? renderBranchRow(item, 0)
          : renderLeafRow(item, 0)
      )}
    </div>,
    document.body
  );
}
